type ViewTransitionLike = {
  ready?: Promise<unknown>;
  finished?: Promise<unknown>;
  updateCallbackDone?: Promise<unknown>;
};

type ViewTransitionDocument = {
  startViewTransition?: (update: () => void) => ViewTransitionLike | undefined;
};

/**
 * Runs `update` inside a View Transition when the browser supports it,
 * otherwise runs it directly.
 *
 * A newer transition skips the one still pending: its `ready` promise rejects
 * with `AbortError: Transition was skipped`. The update callback has already
 * run by then, so the rejection is expected. Nothing awaits these promises, so
 * left alone they surface as unhandled rejections and as production errors.
 * Only that AbortError is swallowed; other rejections (such as an error thrown
 * by the update callback) are rethrown so they still reach error reporting.
 */
export function runWithViewTransition(
  update: () => void,
  doc: ViewTransitionDocument | undefined = typeof document === 'undefined'
    ? undefined
    : (document as unknown as ViewTransitionDocument)
): void {
  if (!doc || typeof doc.startViewTransition !== 'function') {
    update();
    return;
  }
  const transition = doc.startViewTransition.call(doc, update);
  transition?.ready?.catch(ignoreSkippedTransition);
  transition?.finished?.catch(ignoreSkippedTransition);
  transition?.updateCallbackDone?.catch(ignoreSkippedTransition);
}

function ignoreSkippedTransition(error: unknown): void {
  if (error instanceof DOMException && error.name === 'AbortError') return;
  throw error;
}
